'use client';

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { useFormHelper } from '@plura/client/utils';
import cn from 'classnames';
import { format } from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { PlacesType } from 'react-tooltip';
import { DayPicker } from 'react-day-picker';
import Icon, { type IconName } from '../../atoms/Icon';
import Tooltip from '../../atoms/Tooltip';
import 'react-day-picker/dist/style.css';
import './datepicker-field.css';

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  error?: string;
  className?: string;
  classLabel?: string;
  classInput?: string;
  icon?: IconName;
  copy?: boolean;
  tooltip?: string;
  place?: PlacesType;
  trailingComponent?: React.ReactNode;
  /** Journey-style format using `MM`, `DD`, `YYYY` (order + separators from the journey). */
  displayFormat?: string;
};

type DateToken = 'MM' | 'DD' | 'YYYY';

function parseStoredDate(raw: unknown): Date | null {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Maps journey tokens to date-fns patterns (`MM` stays; `DD` → `dd`; `YYYY` → `yyyy`). */
function formatDisplayToDateFns(displayFormat: string): string {
  return displayFormat.replace(/DD/g, 'dd').replace(/YYYY/g, 'yyyy');
}

const DEFAULT_DISPLAY_FORMAT = 'MM/dd/yyyy';

/**
 * Pull one date segment off the front of a digit string, zero-padding when the
 * first digit alone already identifies the value (e.g. "5" -> "05" for months,
 * "13" -> "01" + carry "3" so fast typists never get stuck).
 */
function consumeSegment(digits: string, token: DateToken): [string, string] {
  if (token === 'YYYY') return [digits.slice(0, 4), digits.slice(4)];
  const maxVal = token === 'MM' ? 12 : 31;
  if (!digits.length) return ['', ''];
  const first = Number(digits[0]);
  if (first > Math.floor(maxVal / 10)) return [`0${digits[0]}`, digits.slice(1)];
  if (digits.length === 1) return [digits, ''];
  const two = Number(digits.slice(0, 2));
  if (two === 0) return [digits[0], digits.slice(2)];
  if (two > maxVal) return [`0${digits[0]}`, digits.slice(1)];
  return [digits.slice(0, 2), digits.slice(2)];
}

/** Format raw typed input into the display format, inserting separators. */
function formatTyped(raw: string, tokens: DateToken[], sep: string, completeLast = false): string {
  let digits = raw.replace(/\D/g, '');
  const parts: string[] = [];
  for (const token of tokens) {
    if (!digits.length) break;
    const [part, rest] = consumeSegment(digits, token);
    if (!part) break;
    parts.push(part);
    digits = rest;
    if (part.length < token.length) break;
  }
  if (!parts.length) return '';
  const lastIdx = parts.length - 1;
  const lastToken = tokens[lastIdx];
  // Typing a separator finishes the in-progress segment ("1" + "/" -> "01/").
  if (completeLast && lastToken !== 'YYYY' && parts[lastIdx].length === 1 && Number(parts[lastIdx]) > 0) {
    parts[lastIdx] = `0${parts[lastIdx]}`;
  }
  let out = parts.join(sep);
  const lastComplete = parts[parts.length - 1].length === tokens[parts.length - 1].length;
  if (lastComplete && parts.length < tokens.length) out += sep;
  return out;
}

/** A real calendar date from fully typed text, or null while incomplete/invalid. */
function dateFromText(text: string, tokens: DateToken[], sep: string): Date | null {
  const segments = text.split(sep).map((s) => s.trim()).filter(Boolean);
  if (segments.length !== tokens.length) return null;
  let year = 0;
  let month = 0;
  let day = 0;
  tokens.forEach((token, i) => {
    if (token === 'YYYY') year = Number(segments[i]);
    else if (token === 'MM') month = Number(segments[i]);
    else day = Number(segments[i]);
  });
  const yearSegment = segments[tokens.indexOf('YYYY')];
  if (!year || yearSegment.length < 4 || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  const isReal = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  return isReal ? date : null;
}

/** Room for calendar icon: scales with `--text-size` (ConversationWrapper font setting). */
const conversationInputRightPaddingClass =
  'pr-[length:calc(var(--text-size,1.125rem)*1.25+14px)]';

/* Match conversation `conversation-screens-base.css` `.answerInput input` (ShortTextScreen / TextArea). */
const conversationInputClassName = cn(
  'relative block w-full resize-none overflow-hidden border-0 border-b border-[var(--answer-color,var(--text-color))] bg-transparent py-2.5 pl-0 [font-size:100%] [line-height:120%] font-medium !tracking-normal !rounded-none !shadow-none outline-none ring-0 focus:border-[var(--answer-color,var(--text-color))] focus:!ring-0 focus:!ring-offset-0 disabled:cursor-not-allowed disabled:opacity-40 placeholder:font-medium placeholder:[font-size:100%] placeholder:[line-height:120%] placeholder:text-[var(--answer-color,var(--text-color))] text-[var(--answer-color,var(--text-color))]',
  conversationInputRightPaddingClass,
);

const DatePickerInput = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    label,
    className,
    classLabel,
    classInput,
    icon,
    copy,
    tooltip,
    place,
    trailingComponent,
    displayFormat = DEFAULT_DISPLAY_FORMAT,
    ...rest
  } = props;

  const { onChange: helperOnChange, errorMessage, value } = useFormHelper(rest);
  const fieldId = rest.id != null ? String(rest.id) : undefined;
  const fieldName = (rest.name != null ? String(rest.name) : fieldId) ?? undefined;

  const dateFnsPattern = useMemo(() => formatDisplayToDateFns(displayFormat), [displayFormat]);
  const separator = useMemo(() => displayFormat.match(/[^A-Za-z]+/)?.[0] ?? '/', [displayFormat]);
  const tokens = useMemo(
    () => displayFormat.toUpperCase().split(separator).filter(Boolean) as DateToken[],
    [displayFormat, separator],
  );

  const calendarButtonRef = useRef<HTMLButtonElement>(null);
  const lastCommittedRef = useRef<string>(typeof value === 'string' ? value : '');

  const [text, setText] = useState<string>(() => {
    const stored = parseStoredDate(value);
    if (!stored) return '';
    try {
      return format(stored, dateFnsPattern);
    } catch {
      return '';
    }
  });

  const selectedDate = useMemo(() => parseStoredDate(value), [value]);

  // External changes (calendar pick, form reset, resumed conversation)
  // re-derive the typed text; our own commits are skipped to keep the caret.
  useEffect(() => {
    const formVal = typeof value === 'string' ? value : '';
    if (formVal === lastCommittedRef.current) return;
    lastCommittedRef.current = formVal;
    const stored = parseStoredDate(formVal);
    try {
      setText(stored ? format(stored, dateFnsPattern) : '');
    } catch {
      setText('');
    }
  }, [value, dateFnsPattern]);

  const commitDate = (date: Date | null) => {
    const iso = date ? date.toISOString() : '';
    lastCommittedRef.current = iso;
    // Route through the helper's change handler so an existing validation
    // error re-validates (and clears) as the visitor types.
    helperOnChange({
      currentTarget: { type: 'text', value: iso },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const nativeEvent = e.nativeEvent as InputEvent;
    const inputType = nativeEvent.inputType ?? '';
    const isDeletion = inputType.startsWith('delete');
    const atEnd = e.target.selectionStart === raw.length;
    const typedSeparator = !isDeletion && !!nativeEvent.data && /\D/.test(nativeEvent.data);

    // Only auto-format when appending at the end — deletions and mid-string
    // edits keep the text as typed so the caret never jumps.
    const nextText = !isDeletion && atEnd
      ? formatTyped(raw, tokens, separator, typedSeparator)
      : raw;

    setText(nextText);
    commitDate(dateFromText(nextText, tokens, separator));
  };

  const handleBlur = () => {
    const cleaned = formatTyped(text, tokens, separator);
    setText(cleaned.endsWith(separator) ? cleaned.slice(0, -separator.length) : cleaned);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      calendarButtonRef.current?.click();
    }
  };

  const dataAndAriaProps = useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(rest)) {
      if (k.startsWith('data-') || k.startsWith('aria-')) out[k] = v;
    }
    return out as React.InputHTMLAttributes<HTMLInputElement>;
  }, [rest]);

  React.useImperativeHandle(
    ref,
    () => {
      const getInput = () =>
        fieldId && typeof document !== 'undefined'
          ? (document.getElementById(fieldId) as HTMLInputElement | null)
          : null;
      return {
        focus: (options?: FocusOptions) => getInput()?.focus(options),
        blur: () => getInput()?.blur(),
        get value() {
          return getInput()?.value ?? '';
        },
      } as unknown as HTMLInputElement;
    },
    [fieldId],
  );

  const navChevronStyle = {
    width: 'max(1.25rem, calc(var(--text-size, 1.125rem) * 0.7))',
    height: 'max(1.25rem, calc(var(--text-size, 1.125rem) * 0.7))',
  } as const;

  const dayPickerComponents = useMemo(
    () => ({
      /* Default `chevron` points down; rotate so previous = left, next = right. */
      IconLeft: ({ className: c }: { className?: string }) => (
        <Icon name="chevron" className={cn('shrink-0 fill-current rotate-90', c)} style={navChevronStyle} />
      ),
      IconRight: ({ className: c }: { className?: string }) => (
        <Icon name="chevron" className={cn('shrink-0 fill-current -rotate-90', c)} style={navChevronStyle} />
      ),
    }),
    [],
  );

  return (
    <div className={cn('plura-datepicker-field w-full', className)}>
      {label && (
        <label
          className={cn(
            classLabel,
            'mb-3.5 block text-sm font-semibold leading-[24px] text-[#33383f] [.dark-mode_&]:text-[#efefef]',
          )}
        >
          {label}{' '}
          {tooltip && (
            <Tooltip
              className="relative -top-px [&_svg]:fill-[#9a9fa5]"
              content={tooltip}
              place={place ? place : 'right'}
            />
          )}
        </label>
      )}
      <div className="relative">
        <Popover className="relative w-full">
          <input
            id={fieldId}
            name={fieldName}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            className={cn(conversationInputClassName, classInput)}
            value={text}
            onChange={handleTextChange}
            onBlur={handleBlur}
            onKeyDown={handleInputKeyDown}
            placeholder={rest.placeholder ?? ''}
            required={rest.required}
            disabled={rest.disabled}
            {...dataAndAriaProps}
          />
          <PopoverButton
            ref={calendarButtonRef}
            disabled={rest.disabled}
            className="absolute bottom-0 right-0 top-0 flex cursor-pointer items-center justify-center outline-none text-[color-mix(in_srgb,var(--answer-color,var(--text-color))_55%,transparent)] hover:text-[var(--answer-color,var(--text-color))] focus-visible:text-[var(--answer-color,var(--text-color))] transition-colors duration-150 w-[length:calc(var(--text-size,1.125rem)*1.25+10px)] min-w-[2.5rem]"
            aria-label="Open calendar"
          >
            <Icon
              name="calendar-1"
              className="shrink-0 [&_path]:fill-current"
              style={{
                width: 'calc(var(--text-size, 1.125rem) * 1.25)',
                height: 'calc(var(--text-size, 1.125rem) * 1.25)',
              }}
            />
          </PopoverButton>
          <PopoverPanel
            transition
            anchor={{ to: 'bottom start', padding: 12 }}
            className={cn(
              /* Theming: bg / text / radius / font via datepicker-field.css + :root form vars */
              'plura-datepicker-panel z-[1100] p-3 outline-none',
              '[--anchor-gap:8px] [--anchor-padding:12px] origin-top transition duration-150 ease-out',
              'data-[closed]:scale-95 data-[closed]:opacity-0',
            )}
          >
            {({ close }) => (
              <DayPicker
                mode="single"
                selected={selectedDate ?? undefined}
                defaultMonth={selectedDate ?? new Date()}
                showOutsideDays
                components={dayPickerComponents}
                className="border-0"
                onSelect={(d) => {
                  const valid = d instanceof Date && !Number.isNaN(d.getTime());
                  helperOnChange({
                    currentTarget: { type: 'text', value: valid ? d.toISOString() : '' },
                  } as React.ChangeEvent<HTMLInputElement>);
                  close();
                }}
              />
            )}
          </PopoverPanel>
        </Popover>
        {icon && (
          <div className="icon pointer-events-none absolute bottom-0 left-0 top-0 flex h-12 w-12 items-center justify-center">
            <Icon name={icon} className="!size-5 shrink-0" />
          </div>
        )}
        {copy && (
          <button
            type="button"
            className="absolute bottom-0 right-0 top-0 flex h-12 w-12 items-center justify-center [&_svg]:fill-[#6f767e] hover:[&_svg]:fill-[#2a85ff]"
          >
            <Icon name="copy" className="!size-6" />
          </button>
        )}
        {trailingComponent && (
          <div className="absolute right-1 top-1 z-[2]">{trailingComponent}</div>
        )}
        {errorMessage && <p className="mt-1 text-right text-sm text-[#ff6a55]">{errorMessage}</p>}
      </div>
    </div>
  );
});

DatePickerInput.displayName = 'DatePickerInput';

export default DatePickerInput;
